import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Chip, TextField, Typography, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import StageTracker from './StageTracker';
import { SectionLabel, TEXT, PLANNER_COLORS } from '../../shared_components';
import { STATUS_META } from '../constants';
import { formatPlanDate } from '../utils';
import { checkApiStatus } from '../../../../utils_v2';
import { globalGetService } from '../../../../utils_v2/globalApiServices';
import { getStageTemplateStagesApi } from '../apiService';

const INVOICE_STATUSES = ['Not Raised', 'Raised', 'Paid', 'Financially Closed'];

const errorMessage = (response) => (response && response.data && response.data.message) || 'Unable to process the request due to a technical error.';

const Field = ({ label, value }) => (
  <Grid item xs={6}>
    <Typography variant="caption" style={{ color: TEXT.tertiary, display: 'block' }}>{label}</Typography>
    <Typography variant="body2" style={{ fontWeight: 600, color: TEXT.primary, marginTop: 2 }}>{value || '--'}</Typography>
  </Grid>
);

const PlanDetailDialog = ({ plan, canLaunch, onClose, onLaunch, onCancelPlan, onEdit, enqueueSnackbar }) => {
  const statusLabel = plan?.status?.label;

  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);

  useEffect(() => {
    if (!plan?.stage_template?.id) {
      setStages([]);
      setLoadingStages(false);
      return;
    }
    setLoadingStages(true);
    globalGetService(getStageTemplateStagesApi(plan.stage_template.id)).then((response) => {
      setLoadingStages(false);
      if (checkApiStatus(response)) {
        setStages(response?.data?.data || []);
      } else if (enqueueSnackbar) {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  }, [plan?.stage_template?.id]);

  const currentStageId = plan?.current_stage?.id;

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{plan?.name}</Typography>
        <Typography variant="body2" style={{ color: TEXT.secondary, fontWeight: 400, marginTop: 2 }}>
         {plan?.lessor?.name || ''} · {plan?.lessee?.name || ''} · <span style={{ color: STATUS_META[statusLabel]?.color, fontWeight: 700 }}>{statusLabel}</span>
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Field label="Project Type" value={plan?.project_type?.label} />
          <Field label="Client" value={plan?.client?.id === 1 ? plan?.lessor?.name || '' : plan?.client?.id === 2 ? plan?.lessee?.name || '' : ''} />
          <Field label="Planned Start" value={formatPlanDate(plan?.planned_start_date)} />
          <Field label="Expected End" value={formatPlanDate(plan?.planned_end_date)} />
        </Grid>

        <SectionLabel > Stage Progress </SectionLabel>
        {loadingStages ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        ) : stages?.length ? (
          <StageTracker stages={stages} currentStageId={currentStageId} />
        ) : (
          <Typography variant="body2" style={{ color: TEXT.secondary }}>No stage template configured for this plan.</Typography>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        {statusLabel !== 'Closed' && statusLabel !== 'Cancelled' ? (
          <Button color="secondary" onClick={onCancelPlan} style={{ marginRight: 'auto' }}>Cancel Project</Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" color="primary" onClick={onEdit}>Edit</Button>
        {statusLabel === 'Ready to Launch' && canLaunch ? (
          <Button variant="contained" style={{ backgroundColor: PLANNER_COLORS.green, color: '#fff' }} onClick={onLaunch}>Launch Project</Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default PlanDetailDialog;
